import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { Globe, Mail, Lock, ArrowRight, Sparkles, ChevronDown, CheckCircle, UserPlus, Rocket, CreditCard } from 'lucide-react'
import { getPricingLink, getHomeLink, getTryLink } from '@/config/domains'
import { FramedWordmark } from '@/components/FramedLogo'
import { saveTrialToProject } from '@/services/trialService'
import { supabase } from '@/lib/supabase'

interface LoginFormData {
  email: string
  password: string
}

export const Login = () => {
  const { t, i18n } = useTranslation(['auth', 'common'])
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [useMagicLink, setUseMagicLink] = useState(true)
  const [pendingNavigation, setPendingNavigation] = useState(false)
  const [showResetForm, setShowResetForm] = useState(false)
  const [resetStatus, setResetStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [resetError, setResetError] = useState<string | null>(null)
  const [trialConversionInProgress, setTrialConversionInProgress] = useState(false)
  const [trialConversionError, setTrialConversionError] = useState<string | null>(null)
  const [showSignupPrompt, setShowSignupPrompt] = useState(false)
  const [signupPromptEmail, setSignupPromptEmail] = useState('')
  const navigate = useNavigate()
  const { signIn, signInWithMagicLink, resetPassword, user } = useAuth()
  const trialConversionAttempted = useRef(false)

  // Get trial sessionId from URL params (for trial-to-account conversion)
  const sessionId = searchParams.get('sessionId')

  // Check for disabled account redirect
  useEffect(() => {
    const reason = searchParams.get('reason')
    if (reason === 'disabled') {
      setError(
        i18n.language.startsWith('nl')
          ? 'Je account is uitgeschakeld. Neem contact op met je beheerder.'
          : 'Your account has been disabled. Contact your administrator.'
      )
    }
  }, [searchParams, i18n.language])

  // Handle navigation after successful login (including trial conversion)
  // Triggers on: (1) pendingNavigation set after email/password login, or
  // (2) user already present on mount (OAuth redirect back to /login)
  useEffect(() => {

    if ((pendingNavigation || user) && user && !trialConversionAttempted.current) {
      // If we have a trial sessionId, convert it to a project first
      if (sessionId) {
        trialConversionAttempted.current = true
        setTrialConversionInProgress(true)

        saveTrialToProject(sessionId, user.id)
          .then((result) => {
            setTrialConversionInProgress(false)
            if (result.success && result.projectId) {
              navigate(`/project/${result.projectId}?view=discovery`)
            } else {
              console.error('[Login] Trial conversion failed:', result.error)
              setTrialConversionError(result.error || 'Failed to create your project. Please try again.')
              setError(result.error || 'Failed to create your project. Please try again.')
            }
          })
          .catch((err) => {
            console.error('[Login] Trial conversion error:', err)
            setTrialConversionInProgress(false)
            setTrialConversionError('An unexpected error occurred while creating your project.')
            setError('An unexpected error occurred while creating your project.')
          })
      } else {
        navigate('/dashboard')
      }
    }
  }, [pendingNavigation, user, navigate, sessionId])

  const loginForm = useForm<LoginFormData>()

  const currentLanguage = i18n.language?.startsWith('nl') ? 'nl' : 'en'

  const toggleLanguage = () => {
    const newLang = currentLanguage === 'en' ? 'nl' : 'en'
    i18n.changeLanguage(newLang)
    localStorage.setItem('i18nextLng', newLang)
  }

  const handleLogin = async (data: LoginFormData) => {
    setError(null)
    setSuccess(null)
    setLoading(true)

    const { error } = await signIn(data.email, data.password)

    if (error) {
      setError(t('auth:login.errorInvalidCredentials'))
      setLoading(false)
    } else {
      // Wait for user to be loaded before navigating
      setPendingNavigation(true)
    }
  }

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setShowSignupPrompt(false)
    setLoading(true)

    const email = loginForm.getValues('email')

    if (!email) {
      setError(t('auth:login.enterValidEmail'))
      setLoading(false)
      return
    }

    // Check if email exists before sending magic link
    // This prevents auto-creating accounts for unknown emails
    const { data: emailExists, error: checkError } = await supabase.rpc('check_email_exists', {
      p_email: email,
    })

    if (checkError) {
      console.error('[Login] Email check error:', checkError)
      // On error, fall through to send magic link anyway (graceful degradation)
    } else if (!emailExists) {
      setSignupPromptEmail(email)
      setShowSignupPrompt(true)
      setLoading(false)
      return
    }

    // If we have a trial sessionId, include it in the magic link redirect
    // so the trial can be converted after auth
    const redirectUrl = sessionId
      ? `${window.location.origin}/login?sessionId=${sessionId}`
      : undefined

    const { error } = await signInWithMagicLink(email, redirectUrl)

    if (error) {
      setError(t('auth:login.magicLinkError'))
      setLoading(false)
    } else {
      setSuccess(t('auth:login.magicLinkSent'))
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] overflow-hidden">
      {/* Subtle background grid */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, #E5E5E5 1px, transparent 1px),
            linear-gradient(to bottom, #E5E5E5 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Yellow glow accents */}
      <div className="fixed top-20 right-20 w-[400px] h-[400px] bg-[#FFD500] rounded-full blur-[150px] opacity-20 pointer-events-none" />
      <div className="fixed bottom-20 left-20 w-[300px] h-[300px] bg-[#FFD500] rounded-full blur-[120px] opacity-15 pointer-events-none" />

      {/* Navigation */}
      <nav className="relative z-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <Link to={getHomeLink()}>
                <FramedWordmark className="text-2xl" />
              </Link>
            </motion.div>

            {/* Right side */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-4"
            >
              {/* Language toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-framed-neutral-500 hover:text-framed-neutral-800 rounded-lg hover:bg-framed-neutral-200 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase">{currentLanguage === 'nl' ? 'EN' : 'NL'}</span>
              </button>

              {/* Pricing link */}
              <Link
                to={getPricingLink()}
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-framed-neutral-800 bg-framed-neutral-200 hover:bg-framed-neutral-300 rounded-lg transition-colors"
              >
                {currentLanguage === 'nl' ? 'Prijzen' : 'Pricing'}
              </Link>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Trial Conversion Loading Overlay */}
      {trialConversionInProgress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-12 h-12 border-3 border-framed-neutral-300 border-t-[#FFD500] rounded-full mx-auto mb-4"
            />
            <p className="text-lg font-medium text-framed-neutral-800">
              {currentLanguage === 'nl' ? 'Project wordt aangemaakt...' : 'Creating your project...'}
            </p>
            <p className="text-sm text-framed-neutral-500 mt-1">
              {currentLanguage === 'nl' ? 'Even geduld' : 'Please wait'}
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)] px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-framed-neutral-600 bg-framed-neutral-200 rounded-full mb-6"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {sessionId
                ? (currentLanguage === 'nl' ? 'Bewaar je project' : 'Save your project')
                : (currentLanguage === 'nl' ? 'Welkom terug' : 'Welcome back')}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl sm:text-4xl font-semibold text-framed-neutral-800 tracking-tight mb-3"
            >
              {t('auth:login.title')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-framed-neutral-500"
            >
              {t('auth:login.subtitle')}
            </motion.p>
          </div>

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-framed-neutral-300 rounded-2xl p-8 shadow-sm"
          >
            {/* Error message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600"
              >
                <p>{error}</p>
                {trialConversionError && (
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setError(null)
                        setTrialConversionError(null)
                        trialConversionAttempted.current = false
                        setPendingNavigation(true)
                      }}
                      className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-lg text-xs transition-colors"
                    >
                      {currentLanguage === 'nl' ? 'Opnieuw proberen' : 'Try again'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="px-3 py-1.5 text-red-500 hover:text-red-700 text-xs transition-colors"
                    >
                      {currentLanguage === 'nl' ? 'Ga naar dashboard' : 'Go to dashboard'}
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Success message */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-600"
              >
                {success}
              </motion.div>
            )}

            {/* Signup prompt - shown when email doesn't exist */}
            {showSignupPrompt && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6"
              >
                <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                      <UserPlus className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="font-medium text-framed-neutral-800 text-sm">
                        {t('auth:login.noAccountFound')}
                      </p>
                      <p className="text-sm text-framed-neutral-600 mt-1">
                        {t('auth:login.noAccountFoundDescription', { email: signupPromptEmail })}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <a
                      href={getPricingLink()}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-[#FFD500] hover:bg-[#E6C000] text-[#0a0a0a] font-semibold rounded-xl transition-all text-sm shadow-sm hover:shadow-md hover:-translate-y-0.5"
                    >
                      <CreditCard className="w-4 h-4" />
                      {t('auth:login.signupViewPlans')}
                      <ArrowRight className="w-4 h-4 ml-auto" />
                    </a>

                    <a
                      href={getTryLink()}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-framed-neutral-50 text-framed-neutral-700 font-medium rounded-xl border border-framed-neutral-300 transition-all text-sm"
                    >
                      <Rocket className="w-4 h-4" />
                      {t('auth:login.signupTryFree')}
                      <ArrowRight className="w-4 h-4 ml-auto" />
                    </a>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setShowSignupPrompt(false)
                      setSignupPromptEmail('')
                    }}
                    className="mt-3 text-sm text-framed-neutral-500 hover:text-framed-neutral-700 transition-colors w-full text-center"
                  >
                    {t('auth:login.tryDifferentEmail')}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Google Sign-In */}
            <button
              type="button"
              onClick={async () => {
                setError(null)
                setLoading(true)
                try {
                  const redirectUrl = sessionId
                    ? `${window.location.origin}/login?sessionId=${sessionId}`
                    : `${window.location.origin}/login`
                  const { error } = await supabase.auth.signInWithOAuth({
                    provider: 'google',
                    options: { redirectTo: redirectUrl },
                  })
                  if (error) throw error
                } catch (err: any) {
                  setError(err.message || 'Failed to sign in with Google')
                  setLoading(false)
                }
              }}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-3.5 px-6 rounded-xl font-medium text-framed-neutral-700 border border-framed-neutral-300 hover:bg-framed-neutral-50 transition-all disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {currentLanguage === 'nl' ? 'Doorgaan met Google' : 'Continue with Google'}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-framed-neutral-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-framed-neutral-500">
                  {currentLanguage === 'nl' ? 'of' : 'or'}
                </span>
              </div>
            </div>

            {/* Toggle between Magic Link and Password */}
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setUseMagicLink(true)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  useMagicLink
                    ? 'bg-[#FFD500] text-[#0a0a0a] shadow-sm'
                    : 'bg-framed-neutral-200 text-framed-neutral-600 hover:bg-framed-neutral-300'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                {t('auth:login.magicLink')}
              </button>
              <button
                type="button"
                onClick={() => setUseMagicLink(false)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  !useMagicLink
                    ? 'bg-[#FFD500] text-[#0a0a0a] shadow-sm'
                    : 'bg-framed-neutral-200 text-framed-neutral-600 hover:bg-framed-neutral-300'
                }`}
              >
                <Lock className="w-4 h-4" />
                {t('auth:login.password')}
              </button>
            </div>

            {useMagicLink ? (
              /* Magic Link Form */
              <form onSubmit={handleMagicLink} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-framed-neutral-700 mb-2">
                    {t('common:labels.email')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-framed-neutral-500" />
                    </div>
                    <input
                      {...loginForm.register('email', { required: true })}
                      type="email"
                      id="email"
                      className="block w-full pl-12 pr-4 py-3.5 bg-white border border-framed-neutral-300 hover:border-framed-neutral-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFD500]/30 focus:border-[#FFD500] text-framed-neutral-800 placeholder-framed-neutral-500 transition-all"
                      placeholder={t('auth:login.emailPlaceholder')}
                      disabled={loading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 px-6 bg-[#FFD500] hover:bg-[#E6C000] disabled:bg-framed-neutral-400 text-[#0a0a0a] font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-[#FFD500]/20 hover:shadow-[#FFD500]/30 hover:-translate-y-0.5 disabled:shadow-none disabled:translate-y-0 group"
                >
                  {loading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-[#0a0a0a]/30 border-t-[#0a0a0a] rounded-full"
                    />
                  ) : (
                    <>
                      {t('auth:login.sendMagicLink')}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </button>

                <p className="text-sm text-framed-neutral-500 text-center leading-relaxed">
                  {t('auth:login.magicLinkDescription')}
                </p>
              </form>
            ) : (
              /* Password Form */
              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5">
                <div>
                  <label htmlFor="email-password" className="block text-sm font-medium text-framed-neutral-700 mb-2">
                    {t('common:labels.email')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-framed-neutral-500" />
                    </div>
                    <input
                      {...loginForm.register('email', { required: true })}
                      type="email"
                      id="email-password"
                      className="block w-full pl-12 pr-4 py-3.5 bg-white border border-framed-neutral-300 hover:border-framed-neutral-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFD500]/30 focus:border-[#FFD500] text-framed-neutral-800 placeholder-framed-neutral-500 transition-all"
                      placeholder={t('auth:login.emailPlaceholder')}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-framed-neutral-700 mb-2">
                    {t('common:labels.password')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-framed-neutral-500" />
                    </div>
                    <input
                      {...loginForm.register('password', { required: true })}
                      type="password"
                      id="password"
                      className="block w-full pl-12 pr-4 py-3.5 bg-white border border-framed-neutral-300 hover:border-framed-neutral-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFD500]/30 focus:border-[#FFD500] text-framed-neutral-800 placeholder-framed-neutral-500 transition-all"
                      placeholder={t('auth:login.passwordPlaceholder')}
                      disabled={loading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 px-6 bg-[#FFD500] hover:bg-[#E6C000] disabled:bg-framed-neutral-400 text-[#0a0a0a] font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-[#FFD500]/20 hover:shadow-[#FFD500]/30 hover:-translate-y-0.5 disabled:shadow-none disabled:translate-y-0 group"
                >
                  {loading ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-[#0a0a0a]/30 border-t-[#0a0a0a] rounded-full"
                    />
                  ) : (
                    <>
                      {t('auth:login.submit')}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetForm(!showResetForm)
                      setResetStatus('idle')
                      setResetError(null)
                    }}
                    className="text-sm text-framed-neutral-500 hover:text-[#0a0a0a] transition-colors inline-flex items-center gap-1"
                  >
                    {t('auth:login.forgotPassword')}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showResetForm ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* Password Reset Form */}
                <AnimatePresence>
                  {showResetForm && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 border-t border-framed-neutral-200 space-y-4">
                        {resetStatus === 'success' ? (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-600 flex items-center gap-2"
                          >
                            <CheckCircle className="w-5 h-5 flex-shrink-0" />
                            {t('auth:resetPassword.successMessage')}
                          </motion.div>
                        ) : (
                          <>
                            {resetError && (
                              <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600"
                              >
                                {resetError}
                              </motion.div>
                            )}
                            <p className="text-sm text-framed-neutral-500">
                              {t('auth:resetPassword.subtitle')}
                            </p>
                            <button
                              type="button"
                              disabled={resetStatus === 'loading' || !loginForm.getValues('email')}
                              onClick={async () => {
                                const email = loginForm.getValues('email')
                                if (!email) {
                                  setResetError(t('auth:login.enterValidEmail'))
                                  return
                                }
                                setResetStatus('loading')
                                setResetError(null)
                                // Pass sessionId to the reset password redirect URL if present
                                // This ensures trial data is not lost during password reset
                                const redirectUrl = sessionId
                                  ? `${window.location.origin}/set-password?sessionId=${sessionId}`
                                  : undefined
                                const { error } = await resetPassword(email, redirectUrl)
                                if (error) {
                                  setResetStatus('error')
                                  setResetError(t('auth:resetPassword.errorGeneric'))
                                } else {
                                  setResetStatus('success')
                                }
                              }}
                              className="w-full py-3 px-4 bg-framed-neutral-200 hover:bg-framed-neutral-300 disabled:bg-framed-neutral-200 disabled:text-framed-neutral-500 text-framed-neutral-800 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                              {resetStatus === 'loading' ? (
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                  className="w-5 h-5 border-2 border-framed-neutral-400 border-t-framed-neutral-600 rounded-full"
                                />
                              ) : (
                                <>
                                  <Mail className="w-4 h-4" />
                                  {t('auth:resetPassword.submit')}
                                </>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            )}

            {/* Pricing Link */}
            <div className="mt-8 pt-6 border-t border-framed-neutral-200 text-center">
              <p className="text-sm text-framed-neutral-500">
                {t('auth:login.noAccount')}{' '}
                <Link
                  to={getPricingLink()}
                  className="text-[#0a0a0a] hover:text-[#FFD500] font-semibold transition-colors"
                >
                  {t('auth:login.viewPlans')}
                </Link>
              </p>
            </div>
          </motion.div>

          {/* Footer */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-sm text-framed-neutral-500 mt-8"
          >
            &copy; {new Date().getFullYear()} Framed. {currentLanguage === 'nl' ? 'Alle rechten voorbehouden.' : 'All rights reserved.'}
          </motion.p>
        </motion.div>
      </div>
    </div>
  )
}
